/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IDialogContentStyleProps, IDialogContentStyles } from '@fluentui/react/lib/Dialog';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { logNextWizardStep } from 'src/core/services/telemetry/eventsNamesTemplate';
import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { Region } from 'src/core/types/regionTypes';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation, getPortalFromUrl } from 'src/core/utils/routingUtils';
import { AutoLabelingModalStepsNavigator } from 'src/customText/verticals/autotagTriggerModal/components/AutoLabelingModalStepsNavigator';
import { ConfigureAutoLabelingJob } from 'src/customText/verticals/autotagTriggerModal/components/ConfigureAutoLabelingJob';
import { ReviewAndFinishScreen } from 'src/customText/verticals/autotagTriggerModal/components/ReviewAndFinishScreen';
import { SelectAutoLabelingJobType } from 'src/customText/verticals/autotagTriggerModal/components/SelectAutoLabelingJobType';
import { SelectClassesScreen } from 'src/customText/verticals/autotagTriggerModal/components/SelectClassesScreen';
import { SelectEntitiesScreen } from 'src/customText/verticals/autotagTriggerModal/components/SelectEntitiesScreen';
import { SelectFilesScreen } from 'src/customText/verticals/autotagTriggerModal/components/SelectFilesScreen';
import { AutoLabelingInfoDto, AutoLabelingSteps } from 'src/customText/verticals/autotagTriggerModal/types';
import { isGptFeatureDisabled } from 'src/modules/gptPrediction/utils/gptUtils';
import styled from 'styled-components';

const messages = defineMessages({
    modalTitle: { id: 'customText.autoLabelingModal.modalTitle', defaultMessage: 'Start an auto-labeling job' }
});

const modalTheme = getScopedTheme('ProjectCreationModalBase');
const dialogContentStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({
    inner: { paddingTop: '0px', paddingBottom: '0px', borderTop: `solid 1px ${modalTheme.borderColor}` }
});
const contentWrapperStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { minWidth: 0, width: '100%', paddingTop: '28px', minHeight: '600px', position: 'relative', color: modalTheme.defaultTextColor }
});

const NavigatorWrapper = styled(Stack.Item)<{ $horizontal: boolean }>(props => ({
    paddingTop: '34px',
    width: props.$horizontal ? '300px' : '',
    borderRight: props.$horizontal ? `solid 1px ${modalTheme.borderColor}` : ''
}));

const getInitialAutoLabelingDto = (keyType: ServiceKey['keyType'], resourceRegion: Region): AutoLabelingInfoDto => ({
    documents: [],
    selectedChars: 0,
    classesNames: [],
    entitiesNames: [],
    trainedModelLabel: null,
    azureOpenAiDeploymentName: '',
    azureOpenAiResourceRegion: '',
    autoLabelingJobType: isGptFeatureDisabled(keyType, resourceRegion) ? null : 'gpt'
});

export const AutoLabelingModal = withModalMounter(
    observer((props: BasicModalProps<object>) => {
        const { onDismiss, onSubmit } = props;
        const history = useHistory();
        const { pathname } = useLocation();
        const { localize } = useLocalization();
        const { serviceName } = getPortalFromUrl();
        const { isLargeOrGreater } = useResponsiveMode();
        const { dispatcher, rootStore } = useGlobalState();

        const { keyType } = rootStore.serviceKey;
        const { csAccountStore: currentLanguageResource } = rootStore.azureStore.session;
        const { name: currentProjectName, projectKind } = rootStore.currentCustomTextProjectStore;

        const [activeStep, setActiveStep] = React.useState(AutoLabelingSteps.AutoLabelingJob);
        const [autoLabelingInfoDto, updateAutoLabelingInfoDto] = useUpdateState<AutoLabelingInfoDto>(() =>
            getInitialAutoLabelingDto(keyType, currentLanguageResource.region)
        );

        const handleDismissStep = () => {
            logNextWizardStep(serviceName, 'AutoLabelWithGpt', AutoLabelingSteps[activeStep], 'Cancel');
            onDismiss();
        };

        const handleStepSubmissions = (dto: Partial<AutoLabelingInfoDto>) => {
            setActiveStep(activeStep + 1);
            updateAutoLabelingInfoDto(dto);
            logNextWizardStep(serviceName, 'AutoLabelWithGpt', AutoLabelingSteps[activeStep], '');
        };

        const handleReviewAndFinishStep = () => {
            logNextWizardStep(serviceName, 'AutoLabelWithGpt', 'ReviewAndFinish', '');

            if (autoLabelingInfoDto.autoLabelingJobType === 'gpt') {
                dispatcher.dispatch('runGpt', {
                    toastData: {},
                    classes: autoLabelingInfoDto.classesNames,
                    extractors: autoLabelingInfoDto.entitiesNames,
                    documentsNames: autoLabelingInfoDto.documents.map(d => d.name),
                    azureOpenAiDeploymentName: autoLabelingInfoDto.azureOpenAiDeploymentName,
                    azureOpenAiResourceRegion: autoLabelingInfoDto.azureOpenAiResourceRegion
                });
            } else {
                dispatcher.dispatch('autoTagCustomTextModel', {
                    trainedModelLabel: autoLabelingInfoDto.trainedModelLabel,
                    documents: autoLabelingInfoDto.documents,
                    extractors: autoLabelingInfoDto.entitiesNames
                });
            }
            onSubmit(autoLabelingInfoDto);

            const autoTagRoutesMatchCondition = doesRouteMatchLocation(
                routeTable.customText.autoTaggingPage(currentProjectName, projectKind),
                { locationToMatch: pathname }
            );

            if (!autoTagRoutesMatchCondition) {
                history.push(routeTable.customText.autoTaggingPage(currentProjectName, projectKind));
            }
        };

        const getStepContent = () => {
            switch (activeStep) {
                case AutoLabelingSteps.AutoLabelingJob:
                    return (
                        <SelectAutoLabelingJobType
                            onDismiss={handleDismissStep}
                            autoLabelingInfoDto={autoLabelingInfoDto}
                            onSubmit={autoLabelingJobType => handleStepSubmissions({ autoLabelingJobType })}
                        />
                    );
                case AutoLabelingSteps.ConfigureJob:
                    return (
                        <ConfigureAutoLabelingJob
                            onDismiss={handleDismissStep}
                            autoLabelingInfoDto={autoLabelingInfoDto}
                            onPrevious={() => setActiveStep(activeStep - 1)}
                            onSubmit={dto =>
                                handleStepSubmissions({
                                    trainedModelLabel: dto.trainedModelName,
                                    azureOpenAiResourceRegion: dto.azureOpenAiResourceRegion,
                                    azureOpenAiDeploymentName: dto.azureOpenAiDeploymentName
                                })
                            }
                        />
                    );
                case AutoLabelingSteps.SelectEntitiesOrClasses:
                    return projectKind === 'CustomEntityRecognition' ? (
                        <SelectEntitiesScreen
                            onDismiss={handleDismissStep}
                            autotagInfoDto={autoLabelingInfoDto}
                            onPrevious={() => setActiveStep(activeStep - 1)}
                            onSubmit={entitiesNames => handleStepSubmissions({ entitiesNames })}
                        />
                    ) : (
                        <SelectClassesScreen
                            onDismiss={handleDismissStep}
                            autotagInfoDto={autoLabelingInfoDto}
                            onPrevious={() => setActiveStep(activeStep - 1)}
                            onSubmit={classesNames => handleStepSubmissions({ classesNames })}
                        />
                    );
                case AutoLabelingSteps.SelectFiles:
                    return (
                        <SelectFilesScreen
                            onDismiss={handleDismissStep}
                            autotagInfoDto={autoLabelingInfoDto}
                            onSubmit={(documents, selectedChars) => handleStepSubmissions({ documents, selectedChars })}
                            onPrevious={() => setActiveStep(activeStep - 1)}
                        />
                    );

                case AutoLabelingSteps.ReviewAndFinish:
                    return (
                        <ReviewAndFinishScreen
                            onDismiss={handleDismissStep}
                            autotagInfoDto={autoLabelingInfoDto}
                            onSubmit={handleReviewAndFinishStep}
                            onPrevious={() => setActiveStep(activeStep - 1)}
                        />
                    );

                default:
            }
        };

        return (
            <BasicModal
                {...props}
                minWidth={1200}
                shouldRenderFooter={false}
                dialogContentProps={{ title: localize(messages.modalTitle), styles: dialogContentStyles }}>
                <Stack horizontal={isLargeOrGreater} verticalFill>
                    <NavigatorWrapper $horizontal={isLargeOrGreater}>
                        <AutoLabelingModalStepsNavigator
                            activeStep={activeStep}
                            horizontal={!isLargeOrGreater}
                            onActiveStepChange={setActiveStep}
                            autoLabelingInfoDto={autoLabelingInfoDto}
                        />
                    </NavigatorWrapper>

                    <Stack grow verticalFill styles={contentWrapperStyles}>
                        {getStepContent()}
                    </Stack>
                </Stack>
            </BasicModal>
        );
    })
);
